'use client';

import { ReactElement } from 'react';
import SegmentedMeter from './SegmentedMeter';
import { DOCUMENT_LADDER_STEPS, documentLadderPositionOf } from './ladder';
import { TDocumentConfidenceLevel } from './types';

export type TDocumentConfidenceMeterProps = {
  level: TDocumentConfidenceLevel;
  /** Defaults to the width the design uses on a check card. */
  maxWidth?: string | number;
  /** Omit to leave the bars decorative, which is right wherever the level is also stated in text. */
  'aria-label'?: string;
};

/**
 * The identity-document confidence ladder.
 *
 * Takes a level and nothing else about the ladder: the step count, the fill and the tone are all
 * resolved here, so no caller can pair a document level with the wrong number of steps. That is the
 * whole reason this exists rather than callers reaching for `SegmentedMeter` directly.
 *
 * Its biometric counterpart is a separate component rather than an `axis` prop on this one, for two
 * reasons. The level sets differ: only this axis has `Substantial` and `VeryHigh`. And the ladders
 * differ: this one is four steps, biometric is two, so even the levels both axes share sit at
 * different positions. `High` is three of four here and two of two there. `NotCompleted` is on both
 * axes, but even that one renders different text: "Under review" here, "Not completed" there.
 *
 * **Takes no `data-testid`.** It is always drawn inside a check card or a dialog that carries one,
 * so per ADR 72 it is reached from that ancestor rather than given an id of its own to keep unique.
 */
export default function DocumentConfidenceMeter({
  level,
  maxWidth,
  'aria-label': ariaLabel,
}: Readonly<TDocumentConfidenceMeterProps>): ReactElement {
  const { filled, tone } = documentLadderPositionOf(level);

  return (
    <SegmentedMeter
      bars={DOCUMENT_LADDER_STEPS}
      filled={filled}
      tone={tone}
      maxWidth={maxWidth}
      aria-label={ariaLabel}
    />
  );
}
